import { BeckyModel, BeckyRequestPayload, BeckyTopology } from '../../types/becky'
import { flattenStructure, getChildrenKey } from '../../flattenStructure'
import _ from 'lodash'
import { Data, DeepStructure, ModuleFetcher, Topology } from '@wix/thunderbolt-becky-types'
import { getComponentMappers } from './tbElementsSiteAssets'

export const getRequestedPageCompId = (pages: BeckyRequestPayload['pages']): string =>
	_.findKey(pages, (v, k) => k !== 'masterPage') || 'masterPage'

const mergePagesData = (pageData: Data, masterPageData: Data, isResponsive: boolean) => {
	if (!isResponsive) {
		return _.merge({}, masterPageData, pageData)
	} else {
		const minimalMasterPageData = _.mapValues(masterPageData, (dataMap, mapName) => {
			if (mapName === 'document_data') {
				return _.omitBy(dataMap, (dataItem) => dataItem.type === 'Anchor')
			}
			if (mapName === 'component_properties' || mapName === 'connections_data' || mapName === 'behaviors_data') {
				return {}
			}

			return dataMap
		})

		return _.merge({}, minimalMasterPageData, pageData)
	}
}

const getResponsiveCleanMasterPageStructure = (structure: DeepStructure, isMobile: boolean) => {
	const childrenKey = getChildrenKey(structure, isMobile)
	const children = _.filter(structure[childrenKey], (val) =>
		['masterPage', 'SITE_PAGES', 'SITE_HEADER', 'SITE_FOOTER', 'PAGES_CONTAINER'].includes(val.id)
	)
	return flattenStructure({ ...structure, [childrenKey]: children }, isMobile)
}

const getMergedDataWithFlatPageStructure = (
	pages: BeckyRequestPayload['pages'],
	viewMode: BeckyRequestPayload['viewMode'],
	isResponsive: boolean
) => {
	const pageCompId = getRequestedPageCompId(pages)

	return Promise.all([pages.masterPage, pages[pageCompId]]).then(([masterPage, page]) => {
		const isMasterPage = pageCompId === 'masterPage'
		const isMobile = viewMode === 'mobile'
		const data = isMasterPage ? masterPage.data : mergePagesData(page.data, masterPage.data, isResponsive)
		const structure =
			isMasterPage && isResponsive
				? getResponsiveCleanMasterPageStructure(page.structure, isMobile)
				: flattenStructure(page.structure, isMobile)
		const masterPageStructure = flattenStructure(masterPage.structure, isMobile)
		const translations = page.translations
		const masterPageTranslations = masterPage.translations

		return {
			masterPageStructure,
			structure,
			data,
			pageCompId,
			translations,
			masterPageTranslations,
		}
	})
}

export function deStringifyExperiments(beckyExperiments: string | undefined): BeckyModel['experiments'] {
	if (!beckyExperiments) {
		return {}
	}

	const arrayOfExperiments = beckyExperiments.split(',').map((exp) => {
		const expKeyAndValue = exp.split(':')
		return {
			[expKeyAndValue[0]]: expKeyAndValue[1],
		}
	})

	return arrayOfExperiments.reduce((acc, expKeyAndValue) => {
		return Object.assign(acc, expKeyAndValue)
	}, {})
}

const protocolExists = (val: string) => val.startsWith('http')
const protectedProtocol = (val: string) => val.startsWith('https')

export function protectedTopologyValue(topologyValue: string) {
	if (protocolExists(topologyValue)) {
		return protectedProtocol(topologyValue) ? topologyValue : topologyValue.replace('http://', 'https://')
	} else {
		return `https://${topologyValue}`
	}
}
export function environmentAdaptedUrl(
	topologyValue: string,
	{
		isProtectedSession,
		isBrowser,
	}: {
		isProtectedSession: boolean
		isBrowser: boolean
	} = {
		isProtectedSession: true,
		isBrowser: typeof window !== 'undefined',
	}
): string {
	const shouldBeProtected = isBrowser && isProtectedSession

	if (protocolExists(topologyValue)) {
		if (protectedProtocol(topologyValue)) {
			return shouldBeProtected ? topologyValue : topologyValue.replace('https', 'http')
		} else {
			return shouldBeProtected ? topologyValue.replace('http', 'https') : topologyValue
		}
	} else {
		return shouldBeProtected ? `https://${topologyValue}` : `http://${topologyValue}`
	}
}

export function addTrailingSlash(topologyValue: string) {
	return topologyValue.endsWith('/') ? topologyValue : `${topologyValue}/`
}

export function getBeckyTopology(topology: Topology, payload: BeckyRequestPayload): BeckyTopology {
	const hardCodedScriptsUrl = 'static.parastorage.com'
	const fileRepoUrl = topology.fileRepoUrl ? topology.fileRepoUrl.replace('/services', '') : hardCodedScriptsUrl
	return {
		...topology,
		mediaRootUrl: addTrailingSlash(protectedTopologyValue(topology.mediaRootUrl)),
		staticHTMLComponentUrl: payload.staticHTMLComponentUrl!,
		scriptsUrl: addTrailingSlash(environmentAdaptedUrl(hardCodedScriptsUrl)),
		fileRepoUrl: addTrailingSlash(environmentAdaptedUrl(fileRepoUrl)),
	}
}

export const resolveBeckyModel = async ({
	payload,
	topology,
	moduleFetcher,
}: {
	payload: BeckyRequestPayload
	topology: BeckyTopology
	moduleFetcher?: ModuleFetcher
}): Promise<BeckyModel> => {
	const {
		pages,
		viewMode,
		beckyExperiments,
		tbElementsSiteAssets,
		isWixCodeOnPage: isWixCodeOnPageParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		osType,
		qaMode,
		isInSeo,
		isPremiumDomain,
	} = payload

	const isResponsive = payload.isResponsive === 'true'
	const experiments = deStringifyExperiments(beckyExperiments)

	const [
		{ structure, data, pageCompId, translations, masterPageStructure, masterPageTranslations },
		componentsMappers,
	] = await Promise.all([
		getMergedDataWithFlatPageStructure(pages, viewMode, isResponsive),
		getComponentMappers({ moduleFetcher, topology, tbElementsSiteAssets, experiments }),
	])

	return {
		pageCompId,
		structure,
		data,
		metaSiteId,
		siteId,
		viewMode: viewMode || 'desktop',
		topology,
		language: language!,
		originalLanguage: originalLanguage || 'en',
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		isWixSite: isWixSite === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		shouldShowFreemiumBanner: freemiumBannerParam === 'true',
		shouldShowCoBrandingBanner: coBrandingBannerParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		translations,
		componentsMappers,
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		osType: osType!,
		qaMode: qaMode === 'true',
		masterPageStructure,
		masterPageTranslations,
		isInSeo: isInSeo === 'true',
	}
}
